import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import type { DiseaseTaxonomyNode } from "../../types/diseaseTaxonomy.types";
import {
  getDiseaseDisplayName,
  getDiseaseTooltipWithMondo,
  diseaseNameMatchesSearchQuery,
} from "../../utils/diseaseDisplayName";

/** Show inline filter in the popular-diseases dropdown only when the list is large enough to need it */
const POPULAR_DROPDOWN_FILTER_MIN_COUNT = 100;

const POPULAR_DISEASES: Record<string, string[]> = {
  cardiovascular: [
    "heart failure",
    "congestive heart failure",
    "diastolic heart failure",
    "systolic heart failure",
    "advanced heart failure",
    "dilated cardiomyopathy",
    "familial dilated cardiomyopathy",
    "hypertrophic cardiomyopathy",
    "familial hypertrophic cardiomyopathy",
    "cardio-renal syndrome",
    "acute ischemic stroke",
  ],
  renal: [
    "chronic kidney disease",
    "autosomal dominant polycystic kidney disease",
    "acute kidney injury",
    "lupus nephritis",
  ],
};

interface PopularDiseasesListProps {
  nodes: DiseaseTaxonomyNode[];
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  therapeuticArea: string;
  allowSingleSelection?: boolean;
}

const PopularDiseasesList: React.FC<PopularDiseasesListProps> = ({
  nodes,
  selectedIds,
  onSelectionChange,
  therapeuticArea,
  allowSingleSelection = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const filterInputRef = useRef<HTMLInputElement>(null);
  const [panelRect, setPanelRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const updatePanelRect = useCallback(() => {
    if (!isExpanded || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const width = 360;
    let left = r.left;
    const pad = 8;
    if (left + width > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - width - pad);
    }
    setPanelRect({
      top: r.bottom + 4,
      left,
      width,
    });
  }, [isExpanded]);

  useLayoutEffect(() => {
    if (!isExpanded) {
      setPanelRect(null);
      return;
    }
    updatePanelRect();
  }, [isExpanded, updatePanelRect]);

  useEffect(() => {
    if (!isExpanded) return;
    window.addEventListener("resize", updatePanelRect);
    window.addEventListener("scroll", updatePanelRect, true);
    return () => {
      window.removeEventListener("resize", updatePanelRect);
      window.removeEventListener("scroll", updatePanelRect, true);
    };
  }, [isExpanded, updatePanelRect]);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setIsExpanded(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const popularNamesSet = useMemo(() => {
    const names = POPULAR_DISEASES[therapeuticArea.toLowerCase()];
    if (!names) return new Set<string>();
    return new Set(names.map((n) => n.toLowerCase()));
  }, [therapeuticArea]);

  // Build flat list of selectable popular diseases from the master taxonomy (unique by id)
  const selectableItems = useMemo(() => {
    if (popularNamesSet.size === 0) return [];

    const seen = new Set<string>();
    const items: DiseaseTaxonomyNode[] = [];

    for (const n of nodes) {
      const lowerName = n.name.toLowerCase();
      if (popularNamesSet.has(lowerName) && n.depth > 0 && !seen.has(lowerName)) {
        seen.add(lowerName);
        items.push(n);
      }
    }

    return items.sort((a, b) => a.name.localeCompare(b.name));
  }, [nodes, popularNamesSet]);

  const filteredItems = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return selectableItems;
    return selectableItems.filter((n) =>
      diseaseNameMatchesSearchQuery(n.name, q),
    );
  }, [selectableItems, filterQuery]);

  const showDropdownFilter =
    selectableItems.length > POPULAR_DROPDOWN_FILTER_MIN_COUNT;

  // Focus filter input when dropdown opens (only when filter row is shown)
  useEffect(() => {
    if (isExpanded && showDropdownFilter && filterInputRef.current) {
      filterInputRef.current.focus();
    }
    if (!isExpanded) {
      setFilterQuery("");
    }
  }, [isExpanded, showDropdownFilter]);

  const toggleNode = useCallback(
    (nodeId: number) => {
      if (selectedIds.has(nodeId)) {
        const next = new Set(selectedIds);
        next.delete(nodeId);
        onSelectionChange(next);
        // Auto-close when all deselected
        if (next.size === 0) {
          setIsExpanded(false);
        }
        return;
      }

      // Selecting: in single-selection mode, replace; otherwise add
      if (allowSingleSelection) {
        onSelectionChange(new Set([nodeId]));
      } else {
        const next = new Set(selectedIds);
        next.add(nodeId);
        onSelectionChange(next);
      }
    },
    [selectedIds, onSelectionChange, allowSingleSelection]
  );

  if (popularNamesSet.size === 0) return null;

  const dropdownPanel =
    isExpanded &&
    panelRect &&
    createPortal(
      <div
        ref={panelRef}
        className="fixed z-[10000] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-xl overflow-hidden"
        style={{
          top: panelRect.top,
          left: panelRect.left,
          width: panelRect.width,
        }}
        role="listbox"
        aria-label="Frequently used diseases"
      >
        {showDropdownFilter && (
          <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700">
            <div className="relative">
              <input
                ref={filterInputRef}
                type="text"
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                placeholder="Filter diseases..."
                className="w-full pl-7 pr-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-1 focus:ring-orange-400 focus:border-orange-400 outline-none transition"
              />
              <svg
                className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        )}

        <div className="max-h-[min(420px,calc(100vh-120px))] overflow-auto">
          {filteredItems.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-400 dark:text-slate-500 text-center">
              No matching diseases
            </div>
          ) : (
            filteredItems.map((node) => {
              const isChecked = selectedIds.has(node.id);
              const displayName = getDiseaseDisplayName(node.name);

              return (
                <div
                  key={node.id}
                  className={`flex items-center px-3 py-2 cursor-pointer transition-colors ${
                    isChecked
                      ? "bg-orange-50 dark:bg-orange-900/20"
                      : "hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  }`}
                  onClick={() => toggleNode(node.id)}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleNode(node.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-3.5 h-3.5 rounded cursor-pointer border-slate-300 dark:border-slate-500 text-orange-500 focus:ring-orange-400 focus:ring-offset-0 shrink-0 mr-2.5"
                  />
                  <span
                    className={`text-sm ${
                      isChecked
                        ? "font-medium text-orange-700 dark:text-orange-300"
                        : "text-slate-700 dark:text-slate-200"
                    }`}
                    title={getDiseaseTooltipWithMondo(node.name, node.mondo_id)}
                  >
                    {displayName}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>,
      document.body,
    );

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/25 border border-orange-200 dark:border-orange-700/50 rounded-lg hover:bg-orange-100 hover:border-orange-300 dark:hover:bg-orange-800/40 dark:hover:border-orange-600 transition-all duration-200 shrink-0"
        title="Quick-select from commonly used diseases"
        aria-expanded={isExpanded}
        aria-haspopup="listbox"
      >
        <svg
          className="w-4 h-4 text-orange-500 dark:text-orange-400"
          viewBox="0 0 24 24"
          fill="currentColor"
          stroke="none"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
        Frequently Used Diseases
        <span className="text-[10px] text-orange-500 dark:text-orange-400">
          ({selectableItems.length})
        </span>
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-150 ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {dropdownPanel}
    </div>
  );
};

export default React.memo(PopularDiseasesList);
